import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Play, Pause, Edit, MoreHorizontal, Users, Send, Eye, MousePointer, MessageSquare, Workflow, Search, ChevronDown, Copy, Rocket, CheckCircle2 } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  useCampaignsQuery,
  useUpdateStatusMutation,
  useDuplicateCampaignMutation,
  useDeleteCampaignMutation,
  type Campaign,
} from '@/hooks/useCampaigns';
import { toast } from 'sonner';

const tabs = ['All', 'Active', 'Paused', 'Draft'];
const sortOptions = ['Last Modified', 'Name A-Z', 'Name Z-A', 'Date Created'];

const sortMap: Record<string, { sort_by: string; sort_order: string }> = {
  'Last Modified': { sort_by: 'created_at', sort_order: 'desc' },
  'Name A-Z':      { sort_by: 'name',       sort_order: 'asc'  },
  'Name Z-A':      { sort_by: 'name',       sort_order: 'desc' },
  'Date Created':  { sort_by: 'created_at', sort_order: 'asc'  },
};

const statusBadgeClass = (status: string): string => {
  switch (status) {
    case 'active':    return 'cp-badge-green';
    case 'paused':    return 'cp-badge-amber';
    case 'draft':     return 'cp-badge-gray';
    case 'completed': return 'cp-badge-completed';
    default:          return 'cp-badge-gray';
  }
};

const statusLabel = (status: string): string =>
  status.charAt(0).toUpperCase() + status.slice(1);

const formatSchedule = (c: Campaign): string => {
  if (c.schedule_type === 'immediately') return 'Send Immediately';
  if (c.scheduled_at) {
    return new Date(c.scheduled_at).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  }
  return 'Scheduled';
};

export default function CampaignsPage() {
  const navigate = useNavigate();

  const [activeTab,       setActiveTab]       = useState('All');
  const [searchQuery,     setSearchQuery]     = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy,          setSortBy]          = useState('Last Modified');
  const [openMenuId,      setOpenMenuId]      = useState<string | null>(null);
  const [deleteTarget,    setDeleteTarget]    = useState<{ id: string; name: string } | null>(null);

  // 300 ms debounce
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { sort_by, sort_order } = sortMap[sortBy];
  const tabParam = activeTab === 'All' ? 'all' : activeTab.toLowerCase();

  const { data, isLoading } = useCampaignsQuery({
    tab: tabParam,
    search: debouncedSearch || undefined,
    sort_by,
    sort_order,
  });

  const campaigns: Campaign[] = data?.data?.campaigns ?? [];
  const counts = data?.data?.counts ?? { all: 0, active: 0, paused: 0, completed: 0, draft: 0 };

  const tabCount = (tab: string): number => {
    const map: Record<string, number> = {
      Active: counts.active, Paused: counts.paused,
      Draft: counts.draft, Completed: counts.completed,
    };
    return map[tab] ?? 0;
  };

  const updateStatus   = useUpdateStatusMutation();
  const duplicate      = useDuplicateCampaignMutation();
  const deleteMutation = useDeleteCampaignMutation();

  // Close overflow menu on outside click
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handlePause = (id: string, name: string) =>
    updateStatus.mutate({ id, status: 'paused' }, {
      onSuccess: () => toast.success('Campaign paused', { description: `"${name}" has been paused.` }),
      onError:   (e: Error) => toast.error('Failed to pause campaign', { description: e.message }),
    });

  const handleResume = (id: string, name: string) =>
    updateStatus.mutate({ id, status: 'active' }, {
      onSuccess: () => toast.success('Campaign resumed', { description: `"${name}" is now active.` }),
      onError:   (e: Error) => toast.error('Failed to resume campaign', { description: e.message }),
    });

  const handleLaunch = (id: string, name: string) =>
    updateStatus.mutate({ id, status: 'active' }, {
      onSuccess: () => toast.success('Campaign launched', { description: `"${name}" is now active.` }),
      onError:   (e: Error) => toast.error('Failed to launch campaign', { description: e.message }),
    });

  const handleDuplicate = (id: string, name: string) =>
    duplicate.mutate(id, {
      onSuccess: () => toast.success('Campaign duplicated', { description: `A copy of "${name}" has been created.` }),
      onError:   (e: Error) => toast.error('Failed to duplicate campaign', { description: e.message }),
    });

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => { toast.success('Campaign deleted', { description: `"${deleteTarget.name}" has been permanently deleted.` }); setDeleteTarget(null); },
      onError:   (e: Error) => { toast.error('Failed to delete campaign', { description: e.message }); setDeleteTarget(null); },
    });
  };

  return (
    <div className="portal-page cp-page">
      <div className="cp-header">
        <h1 className="cp-title">Campaigns</h1>
        <div className="cp-header-buttons">
          <Link to="/app/workflows/new" className="btn btn-primary btn-sm"><Workflow size={16} /> Build Workflow</Link>
          <Link to="/app/campaigns/new" className="btn btn-secondary btn-sm"><Plus size={16} /> New Campaign</Link>
        </div>
      </div>

      <div className="cp-toolbar">
        <div className="cp-tabs">
          {tabs.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={`cp-tab ${activeTab === tab ? 'cp-tab--active' : ''}`}>
              {tab}
              {tab !== 'All' && <span className="cp-tab-count">{tabCount(tab)}</span>}
            </button>
          ))}
        </div>
        <div className="cp-toolbar-right">
          <div className="cp-search-wrap">
            <Search size={16} className="cp-search-icon" />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              className="cp-search-input"
            />
          </div>
          <div className="cp-sort-wrap">
            <select
              value={sortBy}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSortBy(e.target.value)}
              className="cp-sort-select"
            >
              {sortOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <ChevronDown size={14} className="cp-sort-chevron" />
          </div>
        </div>
      </div>

      <div className="cp-list">
        {/* Loading skeletons */}
        {isLoading && [1, 2, 3].map(i => (
          <div key={i} className="card cp-card cp-card--skeleton">
            <div className="cp-card-header">
              <div className="cp-card-info">
                <div className="cp-card-name-row">
                  <div className="cp-skeleton-badge" />
                  <div className="cp-skeleton-title" />
                </div>
                <div className="cp-skeleton-subtitle" />
              </div>
            </div>
            <div className="cp-metrics">
              {[1,2,3,4,5].map(j => (
                <div key={j} className="cp-metric-item">
                  <div className="cp-skeleton-metric" />
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Empty state */}
        {!isLoading && campaigns.length === 0 && (
          <div className="cp-empty-state">
            <MessageSquare size={40} className="cp-empty-icon" />
            <p className="cp-empty-title">No campaigns found</p>
            <p className="cp-empty-desc">Create your first campaign to get started.</p>
          </div>
        )}

        {/* Campaign cards */}
        {!isLoading && campaigns.map(campaign => (
          <div key={campaign.id} className={`card cp-card${campaign.sender_profile === null ? ' cp-card--no-sender' : ''}`}>
            <div className="cp-card-header">
              <div className="cp-card-info">
                <div className="cp-card-name-row">
                  <span className={`cp-badge ${statusBadgeClass(campaign.status)}`}>
                    {campaign.status === 'active' && <span className="cp-pulse-dot" />}
                    {statusLabel(campaign.status)}
                  </span>
                  <span className="cp-badge cp-badge-gray cp-badge--sm">{campaign.campaign_id}</span>
                  <Link to={`/app/campaigns/${campaign.id}`} className="cp-card-link">
                    <h3 className="cp-card-title">{campaign.name}</h3>
                  </Link>
                </div>
                <div className="cp-card-subtitle">
                  <Users size={14} />
                  <span>
                    {campaign.segment?.name ?? '—'}
                    {' · '}
                    {formatSchedule(campaign)}
                    {' · '}
                    {campaign.goal?.name ?? '—'}
                    {' · Created '}
                    {new Date(campaign.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    {' '}
                    {new Date(campaign.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                  </span>
                </div>
              </div>

              <div className="cp-card-actions">
                {campaign.status === 'active'    && <button className="btn btn-secondary btn-sm" onClick={() => handlePause(campaign.id, campaign.name)}><Pause size={14} /> Pause</button>}
                {campaign.status === 'paused'    && <button className="btn btn-secondary btn-sm" onClick={() => handleResume(campaign.id, campaign.name)}><Play size={14} /> Resume</button>}
                {campaign.status === 'draft'     && <button className="btn btn-primary btn-sm"    onClick={() => handleLaunch(campaign.id, campaign.name)}><Rocket size={14} /> Launch</button>}
                <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/app/campaigns/${campaign.id}/edit`)}><Edit size={14} /> Edit</button>
                <button className="btn btn-secondary btn-sm" onClick={() => handleDuplicate(campaign.id, campaign.name)}><Copy size={14} /> Duplicate</button>

                {/* Overflow menu */}
                <div className="cp-overflow-wrap" ref={openMenuId === campaign.id ? menuRef : null}>
                  <button
                    className="btn btn-secondary btn-sm cp-more-btn"
                    onClick={() => setOpenMenuId(openMenuId === campaign.id ? null : campaign.id)}
                  >
                    <MoreHorizontal size={16} />
                  </button>
                  {openMenuId === campaign.id && (
                    <div className="cp-dropdown">
                      <button
                        onClick={() => { setOpenMenuId(null); setDeleteTarget({ id: campaign.id, name: campaign.name }); }}
                        className="cp-dropdown-delete"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="cp-metrics">
              {(() => {
                const sent      = campaign.sent ?? 0;
                const delivered = campaign.delivered ?? 0;
                const pctOfSent = (n: number) => sent      > 0 ? Math.round((n / sent)      * 100) : 0;
                const pctOfDel  = (n: number) => delivered > 0 ? Math.round((n / delivered) * 100) : 0;
                return [
                  { label: 'Sent',            value: sent,               rate: null as number | null,   icon: Send,          color: 'var(--navy)',  tooltip: 'Total emails dispatched in this campaign.' },
                  { label: 'Delivered',       value: campaign.delivered, rate: pctOfSent(campaign.delivered), icon: CheckCircle2,  color: 'var(--sky)',   tooltip: 'Percentage of sent emails that successfully reached recipient inboxes.\nFormula: Delivered ÷ Total Sent × 100' },
                  { label: 'Not Replied Yet', value: campaign.opens,     rate: pctOfDel(campaign.opens),      icon: Eye,           color: 'var(--amber)', tooltip: 'Percentage of delivered emails that were read but not yet replied to — indicating actively engaged recipients.\nFormula: (Delivered − Replied) ÷ Delivered × 100' },
                  { label: 'Viewed',          value: campaign.seen,      rate: pctOfDel(campaign.seen),       icon: MousePointer,  color: 'var(--green)', tooltip: 'Percentage of delivered emails that were opened by the recipient.\nFormula: Viewed ÷ Delivered × 100' },
                  { label: 'Replied',         value: campaign.replies,   rate: pctOfDel(campaign.replies),    icon: MessageSquare, color: 'var(--sky)',   tooltip: 'Percentage of delivered emails that received a direct reply from the recipient.\nFormula: Replied ÷ Delivered × 100' },
                ].map(m => (
                  <Tooltip key={m.label}>
                    <TooltipTrigger asChild>
                      <div className="cp-metric-item" style={{ cursor: 'default' }}>
                        <m.icon size={16} color={m.color} className="cp-metric-icon" />
                        <p className="cp-metric-label">{m.label}</p>
                        <p className="cp-metric-value">{m.value.toLocaleString()}</p>
                        {m.rate !== null && <p className="cp-metric-rate">{m.rate}%</p>}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[240px] text-center whitespace-pre-line leading-snug">
                      {m.tooltip}
                    </TooltipContent>
                  </Tooltip>
                ));
              })()}
            </div>
          </div>
        ))}
      </div>

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <div className="cp-confirm-overlay">
          <div className="cp-confirm-dialog">
            <h3 className="cp-confirm-title">Delete Campaign</h3>
            <p className="cp-confirm-text">
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This action cannot be undone.
            </p>
            <div className="cp-confirm-actions">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button
                className="btn btn-primary cp-confirm-delete-btn"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
